import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useGetTaxClasses } from './TaxClasses.ts';
import { getStaleTimeInHours } from '@/shared/utils/staleTime.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';

const STALE_TIME = getStaleTimeInHours(3);

export function useActiveTaxClasses(marketCode: string) {
    const { getTaxClasses } = useGetTaxClasses();

    return useQuery({
        queryKey: ['tax-class', marketCode, 'isActive'],
        queryFn: () => getTaxClasses(marketCode),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
        select: data => data.filter(taxClass => taxClass.isActive),
    });
}

export type TaxClassOptions = {
    value: string;
    label: string;
    description: string;
};

export function useFormattedActiveTaxClassesByMarket() {
    const { marketCode } = useMarketContext();
    const { getTaxClasses } = useGetTaxClasses();

    const query = useSuspenseQuery({
        queryKey: ['tax-class', marketCode, 'isActive', 'formatted'],
        queryFn: () => (marketCode ? getTaxClasses(marketCode) : null),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME,
        select: data => {
            if (!data) {
                return {
                    defaultTaxClass: undefined,
                    taxClassOptions: [],
                };
            }
            const options = data
                .filter(taxClass => taxClass.isActive)
                .map<TaxClassOptions>(taxClass => {
                    return {
                        value: taxClass.name,
                        label: taxClass.rate === 0 ? `${taxClass.name}` : `${taxClass.rate} %`,
                        description: `${taxClass.name} ${taxClass.description}`,
                    };
                });
            return {
                defaultTaxClass: data.find(taxClass => taxClass.isDefault)?.name,
                taxClassOptions: options,
            };
        },
    });
    return query.data;
}

export type FormattedActiveTaxClasses = ReturnType<typeof useFormattedActiveTaxClassesByMarket>;
